import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

// Brand colors
const COLORS = {
  navy: '#1a365d',
  red: '#C7163A',
  blue: '#1a365d',
  beige: '#f5f0e8',
  white: '#FFFFFF',
  lightGray: '#f8f9fa',
  bgGray: '#f0f2f5'
};

// Background graphics URLs - from the prototype
const BG_GRAPHICS = {
  arrow: 'https://customer-assets.emergentagent.com/job_aus-voting-guide/artifacts/y7i3bggl_1.png',
  stars: 'https://customer-assets.emergentagent.com/job_aus-voting-guide/artifacts/cog42c1b_2.png',
  australia: 'https://customer-assets.emergentagent.com/job_aus-voting-guide/artifacts/pbe7hcbw_3.png',
  ballot: 'https://customer-assets.emergentagent.com/job_aus-voting-guide/artifacts/oeqws5l3_4.png'
};

const TPAUS_LOGO = 'https://customer-assets.emergentagent.com/job_134b6b69-11cc-408d-b661-a1cdd383212c/artifacts/zw02imxt_TPAUS%20-%20Logo.png';

// Star Rating Component - using blue for visibility on red background
function StarRating({ rating, size = 20, color = '#1a365d' }) {
  const fullStars = Math.floor(rating || 0);
  const hasHalf = (rating || 0) % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);
  
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(fullStars)].map((_, i) => (
        <svg key={`full-${i}`} width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      ))}
      {hasHalf && (
        <svg width={size} height={size} viewBox="0 0 24 24">
          <defs>
            <linearGradient id="halfGrad">
              <stop offset="50%" stopColor={color}/>
              <stop offset="50%" stopColor="rgba(255,255,255,0.3)"/>
            </linearGradient>
          </defs>
          <path fill="url(#halfGrad)" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <svg key={`empty-${i}`} width={size} height={size} viewBox="0 0 24 24" fill="rgba(255,255,255,0.3)">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      ))}
    </div>
  );
}

// Floating Icon Component
function FloatingIcon({ src, delay, duration, startX, startY, direction }) {
  const style = {
    width: '78px',
    height: '78px',
    animationDelay: `${delay}s`,
    animationDuration: `${duration}s`,
    top: `${startY}%`,
    left: direction === 'ltr' ? `${startX}%` : 'auto',
    right: direction === 'rtl' ? `${startX}%` : 'auto',
  };
  
  return (
    <img 
      src={src} 
      alt="" 
      className={`absolute ${direction === 'ltr' ? 'animate-float-ltr' : 'animate-float-rtl'}`}
      style={style}
    />
  );
}

// Animated Background - with evenly distributed floating icons
function AnimatedBackground() {
  // Grid-based distribution: screen divided into zones
  // Each zone gets icons at staggered times to ensure even coverage
  // Zones: top-left, top-right, middle-left, middle-right, bottom-left, bottom-right
  const icons = [
    // Top-left zone (0-30% x, 5-25% y)
    { src: BG_GRAPHICS.arrow, delay: 1, duration: 20, startX: 5, startY: 10, direction: 'ltr' },
    { src: BG_GRAPHICS.stars, delay: 22, duration: 21, startX: 18, startY: 18, direction: 'ltr' },
    
    // Top-right zone (60-90% x, 5-25% y)
    { src: BG_GRAPHICS.australia, delay: 8, duration: 19, startX: 15, startY: 8, direction: 'rtl' },
    { src: BG_GRAPHICS.ballot, delay: 28, duration: 22, startX: 25, startY: 20, direction: 'rtl' },
    
    // Middle-left zone (0-30% x, 35-55% y)
    { src: BG_GRAPHICS.ballot, delay: 15, duration: 21, startX: 8, startY: 42, direction: 'ltr' },
    { src: BG_GRAPHICS.arrow, delay: 35, duration: 20, startX: 20, startY: 50, direction: 'ltr' },
    
    // Middle-right zone (60-90% x, 35-55% y)
    { src: BG_GRAPHICS.stars, delay: 4, duration: 22, startX: 10, startY: 38, direction: 'rtl' },
    { src: BG_GRAPHICS.australia, delay: 25, duration: 19, startX: 22, startY: 48, direction: 'rtl' },
    
    // Bottom-left zone (0-30% x, 65-85% y)
    { src: BG_GRAPHICS.australia, delay: 11, duration: 19, startX: 12, startY: 72, direction: 'ltr' },
    { src: BG_GRAPHICS.stars, delay: 32, duration: 21, startX: 5, startY: 80, direction: 'ltr' },
    
    // Bottom-right zone (60-90% x, 65-85% y)
    { src: BG_GRAPHICS.arrow, delay: 18, duration: 20, startX: 18, startY: 68, direction: 'rtl' },
    { src: BG_GRAPHICS.ballot, delay: 38, duration: 22, startX: 8, startY: 78, direction: 'rtl' },
  ];

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ backgroundColor: COLORS.bgGray }}>
      {icons.map((icon, idx) => (
        <FloatingIcon key={idx} {...icon} />
      ))}
      
      <style>{`
        .animate-float-ltr, .animate-float-rtl {
          opacity: 0;
        }
        @keyframes float-ltr {
          0% { 
            opacity: 0; 
            transform: translateX(0) translateY(0);
          }
          2.5% { 
            opacity: 0.5; 
          }
          22.5% { 
            opacity: 0.5; 
          }
          25% { 
            opacity: 0; 
          }
          100% { 
            opacity: 0; 
            transform: translateX(25vw) translateY(-15px);
          }
        }
        @keyframes float-rtl {
          0% { 
            opacity: 0; 
            transform: translateX(0) translateY(0);
          }
          2.5% { 
            opacity: 0.5; 
          }
          22.5% { 
            opacity: 0.5; 
          }
          25% { 
            opacity: 0; 
          }
          100% { 
            opacity: 0; 
            transform: translateX(-25vw) translateY(-10px);
          }
        }
        .animate-float-ltr { 
          animation: float-ltr linear infinite;
        }
        .animate-float-rtl { 
          animation: float-rtl linear infinite;
        }
      `}</style>
    </div>
  );
}

// Landing Screen
function LandingScreen({ onSearch }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddressLookup, setShowAddressLookup] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const debounceRef = useRef(null);

  const handleSearch = async (value) => {
    setQuery(value);
    if (!value || value.length < 2) {
      setResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/voting-guide/search?q=${encodeURIComponent(value)}`);
        setResults(res.data.results || []);
        setShowDropdown(true);
      } catch (e) {
        console.error(e);
        setResults([]);
      }
      setSearching(false);
    }, 300);
  };

  const handleSelect = (result) => {
    onSearch(result.electorate);
    setShowDropdown(false);
  };

  const handleFindElectorate = () => {
    setShowAddressLookup(true);
    setAddressInput('');
    setLookupResult(null);
    setLookupError('');
  };

  const handleAddressLookup = async () => {
    if (!addressInput || addressInput.length < 5) {
      setLookupError('Please enter a valid street address');
      return;
    }
    
    setLookingUp(true);
    setLookupError('');
    setLookupResult(null);
    
    try {
      const res = await api.post('/voting-guide/lookup-address', { address: addressInput });
      if (res.data.success) {
        setLookupResult(res.data);
      } else {
        setLookupError(res.data.message || 'Could not find electorate for this address');
      }
    } catch (e) {
      console.error(e);
      setLookupError('Failed to look up address. Please try again.');
    }
    setLookingUp(false);
  };

  const handleSelectLookupResult = () => {
    if (lookupResult?.electorate) {
      onSearch(lookupResult.electorate);
      setShowAddressLookup(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div 
        className="w-full max-w-md bg-white rounded-2xl p-8 relative z-10"
        style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}
      >
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src={TPAUS_LOGO} alt="Turning Point Australia" className="h-16 object-contain" />
        </div>
        
        {/* Title */}
        <h1 className="text-3xl font-bold text-center mb-1" style={{ color: COLORS.navy }}>
          Informed Vote
        </h1>
        <p className="text-center text-gray-400 mb-8 text-sm">
          by Turning Point Australia
        </p>
        
        {/* Search */}
        <div className="relative">
          <label className="block text-base font-semibold mb-2" style={{ color: COLORS.navy }}>
            Find your electorate
          </label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </div>
            <Input
              placeholder="e.g. 5000 / Croydon / Peter Malinauskas"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => results.length > 0 && setShowDropdown(true)}
              className="w-full pl-10 py-3 text-base border-gray-200 rounded-lg"
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Search by writing your postcode, electorate name, or your local State MP's name
          </p>
          {searching && (
            <div className="absolute right-3 top-[38px]">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin"></div>
            </div>
          )}
          
          {/* Results Dropdown */}
          {showDropdown && results.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-xl border max-h-64 overflow-y-auto">
              {results.map((result, idx) => (
                <button
                  key={idx}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0 transition-colors"
                  onClick={() => handleSelect(result)}
                >
                  <div className="font-medium text-gray-800">{result.label}</div>
                  <div className="text-sm text-gray-500">{result.sublabel}</div>
                  {/* Only show postcodes if user is searching with numbers */}
                  {result.postcodes_display && /\d/.test(query) && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      Postcodes: {result.postcodes_display}
                    </div>
                  )}
                </button>
              ))}
              
              {/* Find electorate link */}
              <button
                className="w-full px-4 py-3 text-left hover:bg-red-50 border-t transition-colors"
                onClick={handleFindElectorate}
              >
                <div className="text-sm font-medium" style={{ color: COLORS.red }}>
                  Can't find your electorate? Look it up here →
                </div>
              </button>
            </div>
          )}
          
          {/* Show "find electorate" when no results but query exists */}
          {showDropdown && results.length === 0 && query.length >= 2 && !searching && (
            <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-xl border">
              <div className="px-4 py-3 text-gray-500 text-sm">No electorates found</div>
              <button
                className="w-full px-4 py-3 text-left hover:bg-red-50 border-t transition-colors"
                onClick={handleFindElectorate}
              >
                <div className="text-sm font-medium" style={{ color: COLORS.red }}>
                  Can't find your electorate? Look it up here →
                </div>
              </button>
            </div>
          )}
        </div>
        
        {/* Find Electorate Button */}
        <Button
          onClick={handleFindElectorate}
          className="w-full mt-6 py-6 text-base font-medium rounded-lg flex items-center justify-center gap-2"
          style={{ backgroundColor: COLORS.red, color: 'white' }}
        >
          Find my electorate
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </Button>
      </div>
      
      {/* Address Lookup Modal */}
      {showAddressLookup && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
          onClick={(e) => e.target === e.currentTarget && setShowAddressLookup(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ color: COLORS.navy }}>
                Find Your Electorate
              </h3>
              <button 
                onClick={() => setShowAddressLookup(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            {!lookupResult ? (
              <>
                <p className="text-gray-600 mb-4 text-sm">
                  Enter your street address and we'll find your SA state electorate.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <Input
                      placeholder="e.g., 123 King William St, Adelaide SA 5000"
                      value={addressInput}
                      onChange={(e) => setAddressInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddressLookup()}
                      className="w-full"
                      disabled={lookingUp}
                    />
                  </div>
                  
                  {lookupError && (
                    <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                      {lookupError}
                    </div>
                  )}
                  
                  <Button
                    onClick={handleAddressLookup}
                    disabled={lookingUp || !addressInput}
                    className="w-full py-5"
                    style={{ backgroundColor: COLORS.red, color: 'white' }}
                  >
                    {lookingUp ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Looking up...
                      </div>
                    ) : (
                      'Find Electorate'
                    )}
                  </Button>
                </div>
                
                <p className="text-xs text-gray-400 mt-4 text-center">
                  Powered by AI • Results may vary
                </p>
              </>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    <span className="font-semibold text-green-800">Electorate Found!</span>
                  </div>
                  <div className="text-lg font-bold" style={{ color: COLORS.navy }}>
                    {lookupResult.electorate}
                  </div>
                  {lookupResult.mp_name && (
                    <div className="text-sm text-gray-600 mt-1">
                      MP: {lookupResult.mp_name} ({lookupResult.mp_party})
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setLookupResult(null);
                      setAddressInput('');
                    }}
                    className="flex-1"
                  >
                    Try Another
                  </Button>
                  <Button
                    onClick={handleSelectLookupResult}
                    className="flex-1"
                    style={{ backgroundColor: COLORS.red, color: 'white' }}
                  >
                    View Guide
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Electorate Page
function ElectoratePage({ electorate, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [showHTV, setShowHTV] = useState(false);
  const [showMatrixModal, setShowMatrixModal] = useState(false);
  const [showDownloadToast, setShowDownloadToast] = useState(false);

  // Detect Android device
  const isAndroid = /android/i.test(navigator.userAgent);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/voting-guide/electorate/${encodeURIComponent(electorate)}`);
        setData(res.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetchData();
  }, [electorate]);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (showDownloadToast) {
      const timer = setTimeout(() => setShowDownloadToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showDownloadToast]);

  const trackDownload = async (type, format) => {
    try {
      await api.post(`/voting-guide/track-download?electorate_name=${encodeURIComponent(electorate)}&download_type=${type}&file_format=${format}`);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDownload = async (url, type, format, filename) => {
    trackDownload(type, format);
    
    // Construct full URL if relative
    let downloadUrl = url;
    if (downloadUrl.startsWith('/')) {
      downloadUrl = window.location.origin + downloadUrl;
    }
    
    try {
      // Fetch the file as a blob
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      
      // Create object URL from blob (this makes it same-origin)
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create hidden anchor and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || `${type}.${format}`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      // Show success toast on Android
      if (isAndroid) {
        setShowDownloadToast(true);
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('Download failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gray-300 border-t-red-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <h2 className="text-xl font-bold mb-4" style={{ color: COLORS.navy }}>Electorate Not Found</h2>
          <Button onClick={onBack} style={{ backgroundColor: COLORS.red, color: 'white' }}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      {/* Android Download Success Toast */}
      {showDownloadToast && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[200] download-toast-animation">
          <div className="flex items-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <span className="font-medium">Download Successful</span>
          </div>
        </div>
      )}
      
      <div 
        className="w-full max-w-lg bg-white rounded-2xl overflow-hidden relative z-10"
        style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' }}
      >
        {/* Header - beige background with blue text */}
        <div className="p-4 flex items-center gap-3" style={{ backgroundColor: COLORS.beige }}>
          <button onClick={onBack} className="hover:opacity-70" style={{ color: COLORS.navy }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div className="flex-1">
            <div className="text-sm" style={{ color: COLORS.navy, opacity: 0.7 }}>Electorate of</div>
            <div className="text-xl font-bold" style={{ color: COLORS.navy }}>{data.electorate_name}</div>
          </div>
          <img src={TPAUS_LOGO} alt="TPAUS" className="h-10 object-contain" />
        </div>
        
        {/* MP Card */}
        <div className="p-4" style={{ backgroundColor: COLORS.red }}>
          <div className="flex items-center gap-4">
            {data.mp_photo_url ? (
              <img 
                src={data.mp_photo_url} 
                alt={data.mp_name}
                className="w-20 h-20 rounded-full object-cover border-2 border-white"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
            )}
            <div className="flex-1 text-white">
              <h2 className="text-xl font-bold">{data.mp_name || 'Unknown MP'}</h2>
              <div className="text-white/80">{data.mp_party || 'Unknown Party'}</div>
              {data.star_rating && (
                <div className="mt-2">
                  <StarRating rating={data.star_rating} size={18} color="#1a365d" />
                </div>
              )}
            </div>
          </div>
          {data.mp_description && (
            <p className="mt-3 text-white/90 text-sm">{data.mp_description}</p>
          )}
        </div>
        
        {/* Content Area */}
        <div className="p-4 space-y-4">
          {/* Transparency Report Section */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => { setShowReport(!showReport); if (!showReport) setShowHTV(false); }}
              className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="font-semibold" style={{ color: COLORS.navy }}>Transparency Report</span>
              <svg 
                className={`w-5 h-5 transition-transform ${showReport ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showReport && (
              <div className="p-4 space-y-4">
                {data.voting_report_png_url ? (
                  <>
                    <img 
                      src={data.voting_report_png_url} 
                      alt="Transparency Report"
                      className="w-full rounded-lg border"
                    />
                    <div className="flex gap-2">
                      {data.voting_report_png_url && (
                        <Button
                          onClick={() => handleDownload(data.voting_report_png_url, 'report', 'png', `${data.electorate_name}-transparency-report.png`)}
                          className="flex-1"
                          variant="outline"
                        >
                          Download PNG
                        </Button>
                      )}
                      {data.voting_report_pdf_url && (
                        <Button
                          onClick={() => handleDownload(data.voting_report_pdf_url, 'report', 'pdf', `${data.electorate_name}-transparency-report.pdf`)}
                          className="flex-1"
                          style={{ backgroundColor: COLORS.red, color: 'white' }}
                        >
                          Download PDF
                        </Button>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    Transparency report not yet available for this electorate
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* How to Vote Section */}
          <div className="border rounded-lg overflow-hidden">
            <button
              onClick={() => { setShowHTV(!showHTV); if (!showHTV) setShowReport(false); }}
              className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="font-semibold" style={{ color: COLORS.navy }}>How to Vote Card</span>
              <svg 
                className={`w-5 h-5 transition-transform ${showHTV ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showHTV && (
              <div className="p-4 space-y-4">
                {data.htv_card_png_url ? (
                  <>
                    <img 
                      src={data.htv_card_png_url} 
                      alt="How to Vote Card"
                      className="w-full rounded-lg border"
                    />
                    <div className="flex gap-2">
                      {data.htv_card_png_url && (
                        <Button
                          onClick={() => handleDownload(data.htv_card_png_url, 'htv', 'png', `${data.electorate_name}-how-to-vote.png`)}
                          className="flex-1"
                          variant="outline"
                        >
                          Download PNG
                        </Button>
                      )}
                      {data.htv_card_pdf_url && (
                        <Button
                          onClick={() => handleDownload(data.htv_card_pdf_url, 'htv', 'pdf', `${data.electorate_name}-how-to-vote.pdf`)}
                          className="flex-1"
                          style={{ backgroundColor: COLORS.red, color: 'white' }}
                        >
                          Download PDF
                        </Button>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    How to Vote card not yet available for this electorate
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Values Matrix Button - opens modal */}
          {data.values_matrix_url && (
            <Button
              onClick={() => setShowMatrixModal(true)}
              className="w-full"
              style={{ backgroundColor: COLORS.red, color: 'white' }}
            >
              View Values Matrix
            </Button>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-4 py-3 text-center text-xs text-gray-400 border-t">
          Assessment by Turning Point Australia
        </div>
      </div>
      
      {/* Values Matrix Modal */}
      {showMatrixModal && data.values_matrix_url && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4"
          onClick={(e) => e.target === e.currentTarget && setShowMatrixModal(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-bold" style={{ color: COLORS.navy }}>Values Matrix</h3>
              <button 
                onClick={() => setShowMatrixModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-4">
              <img 
                src={data.values_matrix_url} 
                alt="Values Matrix"
                className="w-full rounded-lg"
              />
            </div>
            
            <div className="p-4 border-t">
              <Button
                onClick={async () => {
                  // Track download
                  api.post(`/voting-guide/track-download?electorate_name=${encodeURIComponent(data.electorate_name)}&download_type=matrix&file_format=png`).catch(() => {});
                  
                  // Construct full URL
                  let imageUrl = data.values_matrix_url;
                  if (imageUrl.startsWith('/')) {
                    imageUrl = window.location.origin + imageUrl;
                  }
                  
                  try {
                    // Fetch the image as a blob
                    const response = await fetch(imageUrl);
                    if (!response.ok) throw new Error('Download failed');
                    const blob = await response.blob();
                    
                    // Create object URL from blob (this makes it same-origin)
                    const blobUrl = window.URL.createObjectURL(blob);
                    
                    // Create hidden anchor and trigger download
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = 'values-matrix.png';
                    document.body.appendChild(link);
                    link.click();
                    
                    // Cleanup
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                    
                    // Show success toast on Android
                    if (isAndroid) {
                      setShowDownloadToast(true);
                    }
                  } catch (err) {
                    console.error('Download error:', err);
                    // Show error to user
                    alert('Download failed. Please try again or right-click the image and select "Save Image As".');
                  }
                }}
                className="w-full py-5"
                style={{ backgroundColor: COLORS.red, color: 'white' }}
              >
                Download the Matrix
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main Component
export default function VotingGuide() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedElectorate, setSelectedElectorate] = useState(searchParams.get('electorate') || null);

  const handleSearch = (electorate) => {
    setSelectedElectorate(electorate);
    setSearchParams({ electorate });
  };

  const handleBack = () => {
    setSelectedElectorate(null);
    setSearchParams({});
  };

  return (
    <div className="relative min-h-screen">
      <AnimatedBackground />
      
      {selectedElectorate ? (
        <ElectoratePage electorate={selectedElectorate} onBack={handleBack} />
      ) : (
        <LandingScreen onSearch={handleSearch} />
      )}
    </div>
  );
}
